/**
 * 文档内搜索 — 高亮匹配的 block，上下导航
 */

import { useState, useMemo } from 'react';
import { Search, X, ChevronUp, ChevronDown } from 'lucide-react';
import { IconButton } from '@hump/ui';
import { useReaderStore } from '../../stores/reader';

export function DocSearch({ docId: _docId }: { docId: string }) {
  const { blocks } = useReaderStore();
  const [query, setQuery] = useState('');
  const [activeIdx, setActiveIdx] = useState(0);
  const [open, setOpen] = useState(false);

  const matches = useMemo(() => {
    if (!query.trim()) return [];
    const q = query.toLowerCase();
    return blocks.filter(
      (b) => b.original.toLowerCase().includes(q) || b.translation.toLowerCase().includes(q)
    );
  }, [blocks, query]);

  function scrollToMatch(idx: number) {
    if (matches.length === 0) return;
    const clamped = Math.max(0, Math.min(idx, matches.length - 1));
    setActiveIdx(clamped);
    const block = matches[clamped];
    const el = document.querySelector(`[data-block-id="${block.id}"]`);
    el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  function handleClose() {
    setOpen(false);
    setQuery('');
    setActiveIdx(0);
  }

  if (!open) {
    return (
      <IconButton size="sm" onClick={() => setOpen(true)} title="搜索文档" aria-label="搜索文档">
        <Search className="w-4 h-4" />
      </IconButton>
    );
  }

  return (
    <div className="flex items-center gap-1">
      <div className="relative">
        <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-charcoal/40" />
        <input
          type="text"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setActiveIdx(0); }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') scrollToMatch(e.shiftKey ? activeIdx - 1 : activeIdx + 1);
            if (e.key === 'Escape') handleClose();
          }}
          autoFocus
          placeholder="搜索..."
          className="w-40 rounded-md border border-border-warm dark:border-border-warm-dark bg-white dark:bg-warm-dark pl-7 pr-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-oasis"
        />
      </div>
      {query && matches.length > 0 && (
        <span className="text-xs text-charcoal/50 dark:text-charcoal-light/50 whitespace-nowrap">
          {activeIdx + 1}/{matches.length}
        </span>
      )}
      {matches.length > 0 && (
        <>
          <IconButton size="sm" onClick={() => scrollToMatch(activeIdx - 1)} aria-label="上一个">
            <ChevronUp className="w-3.5 h-3.5" />
          </IconButton>
          <IconButton size="sm" onClick={() => scrollToMatch(activeIdx + 1)} aria-label="下一个">
            <ChevronDown className="w-3.5 h-3.5" />
          </IconButton>
        </>
      )}
      <IconButton size="sm" onClick={handleClose} aria-label="关闭搜索">
        <X className="w-3.5 h-3.5" />
      </IconButton>
    </div>
  );
}
